import React, { useState } from 'react';
import { SectionCard, Field, TextInput, Select, TextArea, SaveRow, palette } from './ui';
import { GENDERS } from './constants';

export default function BasicInfoCard({ isDarkMode, profile, email, onSave }) {
  const c = palette(isDarkMode);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      headline: profile.headline || '',
      phone: profile.phone || '',
      preferred_location: profile.preferred_location || '',
      date_of_birth: profile.date_of_birth || '',
      gender: profile.gender || '',
      bio: profile.bio || '',
    });
    setEditing(true);
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.first_name.trim()) return;
    setSaving(true);
    const ok = await onSave({ ...form, first_name: form.first_name.trim() });
    setSaving(false);
    if (ok) setEditing(false);
  };

  const rows = [
    ['Name', [profile.first_name, profile.last_name].filter(Boolean).join(' ')],
    ['Headline', profile.headline],
    ['Phone', profile.phone],
    ['Email', email],
    ['Location', profile.preferred_location],
    ['Date of birth', profile.date_of_birth],
    ['Gender', (GENDERS.find(g => g.value === profile.gender) || {}).label],
    ['About', profile.bio],
  ].filter(([, v]) => v);

  return (
    <SectionCard id="basic" title="Basic details" isDarkMode={isDarkMode} onEdit={startEdit} editing={editing}>
      {!editing ? (
        rows.length === 0 ? (
          <p className={`text-sm italic ${c.secondaryText}`}>Add your name and phone number so employers can reach you.</p>
        ) : (
          <dl className="space-y-2">
            {rows.map(([label, value]) => (
              <div key={label} className="flex flex-col sm:flex-row sm:gap-4">
                <dt className={`text-sm w-32 shrink-0 ${c.secondaryText}`}>{label}</dt>
                <dd className={`text-sm ${c.text}`}>{value}</dd>
              </div>
            ))}
          </dl>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name *">
            <TextInput isDarkMode={isDarkMode} value={form.first_name} onChange={set('first_name')} placeholder="Your first name" />
          </Field>
          <Field label="Last name">
            <TextInput isDarkMode={isDarkMode} value={form.last_name} onChange={set('last_name')} placeholder="Your last name" />
          </Field>
          <Field label="Headline" className="sm:col-span-2">
            <TextInput isDarkMode={isDarkMode} value={form.headline} onChange={set('headline')} placeholder="e.g. Delivery executive with 3 years experience" />
          </Field>
          <Field label="Phone *">
            <TextInput isDarkMode={isDarkMode} type="tel" value={form.phone} onChange={set('phone')} placeholder="Mobile number" />
          </Field>
          <Field label="Current location">
            <TextInput isDarkMode={isDarkMode} value={form.preferred_location} onChange={set('preferred_location')} placeholder="City" />
          </Field>
          <Field label="Date of birth">
            <TextInput isDarkMode={isDarkMode} type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
          </Field>
          <Field label="Gender">
            <Select isDarkMode={isDarkMode} value={form.gender} onChange={set('gender')} options={GENDERS} placeholder="Select" />
          </Field>
          <Field label="About you" className="sm:col-span-2">
            <TextArea isDarkMode={isDarkMode} value={form.bio} onChange={set('bio')} placeholder="A few lines about yourself and your work" />
          </Field>
          <div className="sm:col-span-2">
            <SaveRow isDarkMode={isDarkMode} onSave={save} onCancel={() => setEditing(false)} saving={saving} />
          </div>
        </div>
      )}
    </SectionCard>
  );
}
